import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiCheck } from '@mdi/js';
import CustomFormInput from '../../../components/forms/CustomFormInput';
import { classApi } from '../../../api/classes';
import './ConfirmEnrolmentPage.css';

/**
 * ConfirmEnrolmentPage — Public magic-link confirmation page.
 * Route: /confirm-enrolment/:token
 * No auth required — accessed via rollover email link.
 */
const ConfirmEnrolmentPage = () => {
    const { token } = useParams();

    const [enrolment, setEnrolment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConfirming, setIsConfirming] = useState(false);
    const [pageState, setPageState] = useState('loading'); // loading | ready | alreadyConfirmed | confirmed | expired | error
    // Per-class checkbox state: set of enrolmentIds the student wants to
    // confirm. Defaults to all unconfirmed peers ticked when the page loads.
    // Student can untick any class they want to drop without contacting the
    // school; backend only flips the unticked ones (admin will follow up if
    // needed). Already-confirmed peers don't appear as checkboxes; they show
    // as read-only "already confirmed" rows.
    const [selectedIds, setSelectedIds] = useState(new Set());

    useEffect(() => {
        const branding = enrolment?.branding;
        if (!branding) return;
        const root = document.getElementById('confirm-enrolment-root');
        if (!root) return;
        if (branding.headerColor) root.style.setProperty('--school-header-color', branding.headerColor);
        if (branding.headerTextColor) root.style.setProperty('--school-header-text', branding.headerTextColor);
        if (branding.buttonColor) root.style.setProperty('--school-btn-color', branding.buttonColor);
        if (branding.buttonTextColor) root.style.setProperty('--school-btn-text', branding.buttonTextColor);
        if (branding.backgroundColor) root.style.setProperty('--school-bg-color', branding.backgroundColor);
    }, [enrolment]);

    useEffect(() => {
        const fetchPreview = async () => {
            if (!token) {
                setPageState('error');
                setIsLoading(false);
                return;
            }
            try {
                const response = await classApi.getEnrolmentByToken(token);
                if (response.ok) {
                    const enrolmentData = response.data.enrolment || {};
                    setEnrolment({ ...enrolmentData, branding: response.data.branding });
                    // Pre-tick all unconfirmed peers — most students will want
                    // all classes confirmed; unticking is the rare case.
                    const unconfirmedIds = (enrolmentData.classes || [])
                        .filter(c => c.status === 'unconfirmed')
                        .map(c => c.enrolmentId);
                    setSelectedIds(new Set(unconfirmedIds));
                    setPageState(enrolmentData.alreadyConfirmed ? 'alreadyConfirmed' : 'ready');
                } else if (response.status === 410) {
                    setPageState('expired');
                } else {
                    setPageState('error');
                }
            } catch {
                setPageState('error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchPreview();
    }, [token]);

    const toggleClass = (enrolmentId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(enrolmentId)) next.delete(enrolmentId);
            else next.add(enrolmentId);
            return next;
        });
    };

    const handleConfirm = async () => {
        setIsConfirming(true);
        try {
            const response = await classApi.confirmEnrolmentByToken(token, Array.from(selectedIds));
            if (response.ok) {
                // Replace state with the refreshed response so the success view
                // shows exactly which classes ended up confirmed.
                const enrolmentData = response.data.enrolment || {};
                setEnrolment({ ...enrolmentData, branding: response.data.branding });
                setPageState('confirmed');
            } else {
                setPageState('error');
            }
        } catch {
            setPageState('error');
        } finally {
            setIsConfirming(false);
        }
    };

    if (isLoading) {
        return (
            <div className="confirm-enrolment-page" id="confirm-enrolment-root">
                <div className="confirm-enrolment-state-card">
                    <p>Loading your enrolment details...</p>
                </div>
            </div>
        );
    }

    if (pageState === 'expired') {
        return (
            <div className="confirm-enrolment-page" id="confirm-enrolment-root">
                <div className="confirm-enrolment-state-card confirm-enrolment-state-error">
                    <h2>Link Expired</h2>
                    <p>This confirmation link has expired (links are valid for 14 days).</p>
                    <p>Please contact your studio to confirm your enrolment.</p>
                </div>
            </div>
        );
    }

    if (pageState === 'error') {
        return (
            <div className="confirm-enrolment-page" id="confirm-enrolment-root">
                <div className="confirm-enrolment-state-card confirm-enrolment-state-error">
                    <h2>Link Not Found</h2>
                    <p>This confirmation link is invalid or has already been used.</p>
                    <p>Please contact your studio if you need to confirm your enrolment.</p>
                </div>
            </div>
        );
    }

    if (pageState === 'alreadyConfirmed' || pageState === 'confirmed') {
        // Show ONLY classes that are now confirmed — not all peers. Earlier
        // version listed all peer enrolments (including ones the student
        // unticked), saying "all 3 classes" when only 1 was actually flipped.
        const confirmedClasses = (enrolment?.classes || [])
            .filter(c => c.status === 'confirmed')
            .map(c => c.className)
            .filter(Boolean);
        const stillPending = (enrolment?.classes || [])
            .filter(c => c.status === 'unconfirmed')
            .map(c => c.className)
            .filter(Boolean);
        const classList = confirmedClasses.length > 0
            ? confirmedClasses
            : (enrolment?.className ? [enrolment.className] : []);
        return (
            <div className="confirm-enrolment-page" id="confirm-enrolment-root">
                {enrolment?.orgName && (
                    <div className="confirm-enrolment-header">
                        <h1 className="confirm-enrolment-org-name">{enrolment.orgName}</h1>
                        <p className="confirm-enrolment-subtitle">Class Enrolment</p>
                        {enrolment?.studentName && (
                            <p className="confirm-enrolment-student-name">For {enrolment.studentName}</p>
                        )}
                    </div>
                )}
                <div className="confirm-enrolment-card">
                    <div className="confirm-enrolment-state-success">
                        <h2>Enrolment Confirmed</h2>
                        {enrolment?.termName && (
                            <p className="confirm-enrolment-term-line">{enrolment.termName}</p>
                        )}
                        {classList.length > 0 && (
                            <ul className="confirm-enrolment-confirmed-list">
                                {classList.map(name => (
                                    <li key={name}>{name}</li>
                                ))}
                            </ul>
                        )}
                        <p>
                            {classList.length > 1
                                ? `Your place is confirmed in ${classList.length} classes. See you in class!`
                                : 'Your place is confirmed. See you in class!'}
                        </p>
                        {stillPending.length > 0 && (
                            <p className="confirm-enrolment-pending-note">
                                {stillPending.length === 1
                                    ? `${stillPending[0]} was not confirmed. Contact your studio if you'd like to keep it.`
                                    : `${stillPending.length} other classes were not confirmed. Contact your studio if you'd like to keep them.`}
                            </p>
                        )}
                        <p className="confirm-enrolment-app-hint">
                            Open the <strong>Dance Localé app</strong> to view your classes and check in.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="confirm-enrolment-page" id="confirm-enrolment-root">
            <div className="confirm-enrolment-header">
                <h1 className="confirm-enrolment-org-name">{enrolment?.orgName || 'Your Studio'}</h1>
                <p className="confirm-enrolment-subtitle">Class Enrolment Confirmation</p>
                {enrolment?.studentName && (
                    <p className="confirm-enrolment-student-name">For {enrolment.studentName}</p>
                )}
            </div>

            <div className="confirm-enrolment-card">
                {(() => {
                    const allClasses = enrolment?.classes || [];
                    const unconfirmedClasses = allClasses.filter(c => c.status === 'unconfirmed');
                    const alreadyConfirmedClasses = allClasses.filter(c => c.status === 'confirmed');
                    const isMulti = unconfirmedClasses.length > 1 || (unconfirmedClasses.length === 1 && alreadyConfirmedClasses.length > 0);
                    return (
                        <>
                            {/* Term sits at the top as its own emphasised line —
                                makes it the primary context, with the class
                                checkboxes underneath. Replaces the previous
                                comma-list layout where the term ran in with the
                                class names. */}
                            {enrolment?.termName && (
                                <p className="confirm-enrolment-term-line">{enrolment.termName}</p>
                            )}
                            <h2 className="confirm-enrolment-section-title">
                                {isMulti ? 'Confirm your classes' : 'Confirm your class'}
                            </h2>
                            {isMulti && (
                                <p className="confirm-enrolment-helper">
                                    Tick the classes you'd like to confirm. Untick any class you'd like to drop.
                                </p>
                            )}
                            <ul className="confirm-enrolment-class-list">
                                {unconfirmedClasses.map(c => (
                                    <li key={c.enrolmentId} className="confirm-enrolment-class-row">
                                        <CustomFormInput
                                            type="checkbox"
                                            variant="purple"
                                            label={c.className}
                                            checked={selectedIds.has(c.enrolmentId)}
                                            onChange={() => toggleClass(c.enrolmentId)}
                                            disabled={isConfirming}
                                        />
                                    </li>
                                ))}
                                {alreadyConfirmedClasses.map(c => (
                                    <li key={c.enrolmentId} className="confirm-enrolment-class-row confirm-enrolment-class-row--confirmed">
                                        <span className="confirm-enrolment-class-confirmed-label">
                                            <Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" />
                                            {c.className} (already confirmed)
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="confirm-enrolment-notice">
                                Payment will be arranged separately by {enrolment?.orgName || 'your studio'}.
                            </div>

                            <button
                                className="confirm-enrolment-btn"
                                onClick={handleConfirm}
                                disabled={isConfirming || selectedIds.size === 0}
                            >
                                {isConfirming
                                    ? 'Confirming...'
                                    : selectedIds.size === 0
                                        ? 'Select at least one class'
                                        : selectedIds.size === 1
                                            ? 'Confirm 1 Class'
                                            : `Confirm ${selectedIds.size} Classes`}
                            </button>

                            <p className="confirm-enrolment-expiry-note">
                                This link expires 14 days after it was sent. If you cannot confirm now, contact your studio.
                            </p>
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default ConfirmEnrolmentPage;
